import type { ComponentType } from 'react';
import type { Locale } from '../i18n';

// Vite eagerly globs every MDX file at build; lazy-loads its component code.
// Path keys look like '/src/content/en/overview.mdx'.
type ModuleLoader = () => Promise<{ default: ComponentType }>;
const allDocs = import.meta.glob<{ default: ComponentType }>('../content/**/*.mdx') as Record<string, ModuleLoader>;

/** All available slug paths per locale, e.g. { en: ['overview', 'architecture'] }. */
export function listSlugsByLocale(): Record<Locale, string[]> {
  const out: Record<string, string[]> = {};
  for (const key of Object.keys(allDocs)) {
    const m = key.match(/\/content\/([^/]+)\/(.+)\.mdx$/);
    if (!m) continue;
    const [, lang, slug] = m;
    if (!lang || !slug) continue;
    (out[lang] ??= []).push(slug);
  }
  for (const lang of Object.keys(out)) {
    out[lang]!.sort();
  }
  return out as Record<Locale, string[]>;
}

/**
 * Look up the MDX component loader for a given locale and slug, with fallback
 * to English when the requested locale lacks a translation for that slug.
 * Returns null only if neither the requested locale nor English has the page.
 */
export function getDocLoader(locale: Locale, slug: string): ModuleLoader | null {
  const tryPath = (loc: string) => allDocs[`../content/${loc}/${slug}.mdx`];
  return tryPath(locale) ?? tryPath('en') ?? null;
}
